import React, { useEffect, useRef } from 'react';

const ThemeEffects = ({ themeName }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let width = canvas.width = window.innerWidth;
    let height = canvas.height = window.innerHeight;

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    let elements = [];
    let fireworks = [];

    // ─── SNOWFLAKE (Winter / Christmas) ────────────────────────────────────────
    class Snowflake {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -10;
        this.size = Math.random() * 4 + 1;
        this.speed = Math.random() * 1.2 + 0.4;
        this.wind = Math.random() * 0.6 - 0.3;
        this.opacity = Math.random() * 0.6 + 0.2;
        this.angle = 0; this.angleSpeed = (Math.random() - 0.5) * 0.02;
      }
      update() {
        this.y += this.speed; this.x += this.wind + Math.sin(this.angle) * 0.5;
        this.angle += this.angleSpeed;
        if (this.y > height + 10) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle);
        ctx.globalAlpha = this.opacity;
        for (let i = 0; i < 6; i++) {
          ctx.strokeStyle = `rgba(220, 240, 255, ${this.opacity})`;
          ctx.lineWidth = this.size * 0.4;
          ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, this.size * 3);
          ctx.moveTo(0, this.size); ctx.lineTo(this.size * 0.8, this.size * 1.8);
          ctx.moveTo(0, this.size); ctx.lineTo(-this.size * 0.8, this.size * 1.8);
          ctx.stroke(); ctx.rotate(Math.PI / 3);
        }
        ctx.globalAlpha = 1; ctx.restore();
      }
    }

    // ─── FIREWORK (Diwali) ────────────────────────────────────────────────────
    class DiwaliParticle {
      constructor(x, y, color, opts = {}) {
        this.x = x; this.y = y; this.color = color;
        const angle = Math.random() * Math.PI * 2;
        const speed = (Math.random() * 6 + 1.8) * (opts.speedMul || 1);
        this.vx = Math.cos(angle) * speed; this.vy = Math.sin(angle) * speed;
        this.size = (Math.random() * 2.8 + 1) * (opts.sizeMul || 1);
        this.life = 1;
        this.decay = (Math.random() * 0.017 + 0.009) * (opts.decayMul || 1);
        this.gravity = (0.08 + Math.random() * 0.06) * (opts.gravityMul || 1);
        this.friction = 0.985;
        this.trail = [];
        this.brightness = opts.brightness || 1;
      }
      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > 7) this.trail.shift();
        this.x += this.vx; this.y += this.vy;
        this.vy += this.gravity;
        this.vx *= this.friction;
        this.vy *= this.friction;
        this.life -= this.decay;
      }
      draw(ctx) {
        for (let i = 0; i < this.trail.length; i++) {
          const t = i / this.trail.length;
          ctx.beginPath(); ctx.arc(this.trail[i].x, this.trail[i].y, this.size * t, 0, Math.PI * 2);
          ctx.fillStyle = this.color; ctx.globalAlpha = Math.max(0, this.life * t * 0.45 * this.brightness); ctx.fill();
        }
        ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color; ctx.globalAlpha = Math.max(0, this.life * 0.75 * this.brightness); ctx.fill();
        ctx.beginPath(); ctx.arc(this.x, this.y, this.size * 0.45, 0, Math.PI * 2);
        ctx.fillStyle = '#fff7cc'; ctx.globalAlpha = Math.max(0, this.life * 0.9); ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    class DiwaliEmber {
      constructor(x, y, color) {
        this.x = x;
        this.y = y;
        this.color = color;
        this.vx = Math.random() * 1.8 - 0.9;
        this.vy = -(Math.random() * 1.4 + 0.8);
        this.life = 1;
        this.decay = Math.random() * 0.012 + 0.006;
        this.size = Math.random() * 1.8 + 0.6;
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vy += 0.045;
        this.vx *= 0.992;
        this.life -= this.decay;
      }
      draw(ctx) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = Math.max(0, this.life * 0.55);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    class DiwaliRocket {
      constructor() {
        this.x = Math.random() * width * 0.8 + width * 0.1; this.y = height + 5;
        this.vy = -(Math.random() * 7 + 8.5);
        this.vx = Math.random() * 0.8 - 0.4;
        this.targetY = Math.random() * height * 0.5 + height * 0.08;
        this.color = `hsl(${Math.random() * 80 + 10}, 100%, 62%)`;
        this.trail = [];
        this.phase = Math.random() * Math.PI * 2;
      }
      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > 14) this.trail.shift();
        this.phase += 0.09;
        this.x += this.vx + Math.sin(this.phase) * 0.18;
        this.y += this.vy;
        this.vy *= 0.992;
      }
      exploded() { return this.y <= this.targetY; }
      draw(ctx) {
        for (let i = 0; i < this.trail.length; i++) {
          const t = i / this.trail.length;
          ctx.beginPath(); ctx.arc(this.trail[i].x, this.trail[i].y, t * 3.5, 0, Math.PI * 2);
          ctx.fillStyle = '#ffd700'; ctx.globalAlpha = t * 0.45; ctx.fill();
        }
        ctx.beginPath(); ctx.arc(this.x, this.y, 2.4, 0, Math.PI * 2);
        ctx.fillStyle = '#fff6b0'; ctx.globalAlpha = 0.9; ctx.fill();
        ctx.globalAlpha = 1;
      }
      explode() {
        const particles = [];
        const colors = ['#ffd700', '#ff4500', '#00ff88', '#ff69b4', '#00bfff', '#ff1493', '#ffa500', '#facc15'];
        const burstCount = Math.floor(Math.random() * 28) + 66;
        for (let j = 0; j < burstCount; j++) {
          particles.push(new DiwaliParticle(this.x, this.y, colors[Math.floor(Math.random() * colors.length)], {
            speedMul: 1.05,
            sizeMul: 1,
            brightness: 0.95
          }));
        }

        const coreCount = Math.floor(Math.random() * 12) + 20;
        for (let j = 0; j < coreCount; j++) {
          particles.push(new DiwaliParticle(this.x, this.y, '#ffe082', {
            speedMul: 0.6,
            sizeMul: 1.2,
            decayMul: 0.9,
            gravityMul: 0.8,
            brightness: 1
          }));
        }

        const emberCount = Math.floor(Math.random() * 10) + 14;
        for (let j = 0; j < emberCount; j++) {
          particles.push(new DiwaliEmber(this.x, this.y, colors[Math.floor(Math.random() * colors.length)]));
        }
        return particles;
      }
    }

    class DiwaliGlowOrb {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.r = Math.random() * 35 + 18;
        this.vx = Math.random() * 0.28 - 0.14;
        this.vy = Math.random() * 0.22 - 0.11;
        this.phase = Math.random() * Math.PI * 2;
        this.phaseSpeed = Math.random() * 0.016 + 0.006;
        this.hue = Math.random() * 70 + 10;
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.phase += this.phaseSpeed;
        if (this.x < -this.r) this.x = width + this.r;
        if (this.x > width + this.r) this.x = -this.r;
        if (this.y < -this.r) this.y = height + this.r;
        if (this.y > height + this.r) this.y = -this.r;
      }
      draw(ctx) {
        const pulse = 0.45 + 0.35 * Math.abs(Math.sin(this.phase));
        const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.r);
        grad.addColorStop(0, `hsla(${this.hue}, 100%, 62%, ${0.11 * pulse})`);
        grad.addColorStop(0.6, `hsla(${this.hue + 20}, 100%, 58%, ${0.05 * pulse})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ─── HOLI (Color Splashes) ────────────────────────────────────────────────
    const holiColors = ['#ff1493', '#ff6600', '#00cc00', '#9400d3', '#0099ff', '#ffcc00', '#ff4444', '#00e5ff'];
    class ColorDrop {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -20;
        this.size = Math.random() * 8 + 4; this.vy = Math.random() * 4 + 2; this.vx = Math.random() * 2 - 1;
        this.color = holiColors[Math.floor(Math.random() * holiColors.length)];
        this.opacity = Math.random() * 0.5 + 0.3; this.splat = false; this.splatSize = 0; this.splatOpacity = 0;
      }
      update() {
        if (this.splat) {
          this.splatSize += 2; this.splatOpacity -= 0.04;
          if (this.splatOpacity <= 0) this.reset();
        } else {
          this.y += this.vy; this.x += this.vx;
          if (this.y > height * 0.8) { this.splat = true; this.splatOpacity = this.opacity * 0.5; }
        }
      }
      draw(ctx) {
        if (this.splat) {
          ctx.save(); ctx.globalAlpha = Math.max(0, this.splatOpacity); ctx.fillStyle = this.color;
          ctx.beginPath(); ctx.ellipse(this.x, this.y, this.splatSize, this.splatSize * 0.3, 0, 0, Math.PI * 2);
          ctx.fill(); ctx.restore();
        } else {
          ctx.save(); ctx.globalAlpha = this.opacity; ctx.fillStyle = this.color;
          ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); ctx.fill(); ctx.restore();
        }
      }
    }
    class ColorBlob {
      constructor() {
        this.x = Math.random() * width; this.y = Math.random() * height;
        this.radius = Math.random() * 60 + 20;
        this.color = holiColors[Math.floor(Math.random() * holiColors.length)];
        this.vx = Math.random() * 0.4 - 0.2; this.vy = Math.random() * 0.4 - 0.2;
        this.pulse = 0; this.pulseSpeed = Math.random() * 0.04 + 0.01;
      }
      update() {
        this.x += this.vx; this.y += this.vy; this.pulse += this.pulseSpeed;
        if (this.x < -this.radius) this.x = width + this.radius;
        if (this.x > width + this.radius) this.x = -this.radius;
        if (this.y < -this.radius) this.y = height + this.radius;
        if (this.y > height + this.radius) this.y = -this.radius;
      }
      draw(ctx) {
        const r = this.radius + Math.sin(this.pulse) * 10;
        const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, r);
        grad.addColorStop(0, this.color + 'aa'); grad.addColorStop(1, this.color + '00');
        ctx.beginPath(); ctx.arc(this.x, this.y, r, 0, Math.PI * 2); ctx.fillStyle = grad; ctx.fill();
      }
    }

    // ─── NAVRATRI (Marigold Petals) ───────────────────────────────────────────
    const navratriColors = ['#ff8c00', '#ffcc00', '#ff6347', '#ffeaa7', '#e17055', '#fdcb6e'];
    class Petal {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -20;
        this.sizeW = Math.random() * 10 + 5; this.sizeH = Math.random() * 5 + 2;
        this.speed = Math.random() * 1.5 + 0.5; this.opacity = Math.random() * 0.7 + 0.2;
        this.wobble = Math.random() * Math.PI * 2; this.wobbleSpeed = Math.random() * 0.03 + 0.01;
        this.angle = Math.random() * Math.PI * 2; this.angleSpeed = (Math.random() - 0.5) * 0.04;
        this.color = navratriColors[Math.floor(Math.random() * navratriColors.length)];
      }
      update() {
        this.y += this.speed; this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * 2; this.angle += this.angleSpeed;
        if (this.y > height + 20) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle);
        ctx.globalAlpha = this.opacity; ctx.fillStyle = this.color;
        ctx.beginPath(); ctx.ellipse(0, 0, this.sizeW, this.sizeH, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      }
    }

    // ─── EID (Golden Twinkling Stars) ─────────────────────────────────────────
    class EidStar {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = Math.random() * height;
        this.size = Math.random() * 3 + 1; this.opacity = Math.random() * 0.8 + 0.1;
        this.twinkleSpeed = Math.random() * 0.04 + 0.01; this.twinklePhase = Math.random() * Math.PI * 2;
        this.color = Math.random() > 0.5 ? '#d4af37' : '#c0c0c0';
        this.vy = Math.random() * 0.2 - 0.1; this.vx = Math.random() * 0.2 - 0.1;
      }
      update() {
        this.twinklePhase += this.twinkleSpeed; this.x += this.vx; this.y += this.vy;
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
      }
      draw(ctx) {
        const twinkle = 0.4 + 0.6 * Math.abs(Math.sin(this.twinklePhase));
        ctx.save(); ctx.translate(this.x, this.y); ctx.globalAlpha = this.opacity * twinkle;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        for (let i = 0; i < 4; i++) {
          const angle = (i * Math.PI) / 2; const inner = this.size * 0.4;
          ctx.lineTo(Math.cos(angle) * this.size, Math.sin(angle) * this.size);
          ctx.lineTo(Math.cos(angle + Math.PI / 4) * inner, Math.sin(angle + Math.PI / 4) * inner);
        }
        ctx.closePath(); ctx.fill();
        const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size * 2);
        glow.addColorStop(0, this.color + '60'); glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(0, 0, this.size * 2, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      }
    }

    // ─── SPRING (Cherry Blossom Petals) ──────────────────────────────────────
    const springColors = ['#ffb7c5', '#ffc0cb', '#ff85a2', '#ffd7e3', '#ff9db5', '#ffe4e8'];
    class BlossomPetal {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -20;
        this.size = Math.random() * 8 + 4; this.speed = Math.random() * 1 + 0.3;
        this.opacity = Math.random() * 0.7 + 0.2;
        this.wobble = Math.random() * Math.PI * 2; this.wobbleSpeed = Math.random() * 0.025 + 0.01;
        this.angle = Math.random() * Math.PI * 2; this.angleSpeed = (Math.random() - 0.5) * 0.03;
        this.color = springColors[Math.floor(Math.random() * springColors.length)];
      }
      update() {
        this.y += this.speed; this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * 2.5; this.angle += this.angleSpeed;
        if (this.y > height + 20) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle);
        ctx.globalAlpha = this.opacity; ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.moveTo(0, this.size * 0.5);
        ctx.bezierCurveTo(-this.size, 0, -this.size, -this.size, 0, -this.size * 0.5);
        ctx.bezierCurveTo(this.size, -this.size, this.size, 0, 0, this.size * 0.5);
        ctx.fill(); ctx.restore();
      }
    }

    // ─── SUMMER (Rising Bubbles) ──────────────────────────────────────────────
    class SummerBubble {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = height + 20;
        this.r = Math.random() * 20 + 5; this.vy = -(Math.random() * 1.5 + 0.5);
        this.vx = Math.random() * 0.6 - 0.3; this.opacity = Math.random() * 0.3 + 0.05;
      }
      update() {
        this.y += this.vy; this.x += this.vx;
        if (this.y < -this.r * 2) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.globalAlpha = this.opacity;
        const grad = ctx.createRadialGradient(this.x - this.r * 0.3, this.y - this.r * 0.3, 0, this.x, this.y, this.r);
        grad.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
        grad.addColorStop(0.5, 'rgba(255, 200, 100, 0.3)');
        grad.addColorStop(1, 'rgba(255, 120, 50, 0.1)');
        ctx.beginPath(); ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
        ctx.fillStyle = grad; ctx.fill();
        ctx.strokeStyle = 'rgba(255, 160, 80, 0.3)'; ctx.lineWidth = 1; ctx.stroke(); ctx.restore();
      }
    }

    // ─── AUTUMN (Falling Leaves) ──────────────────────────────────────────────
    const autumnColors = ['#d2691e', '#cd853f', '#b8860b', '#8b4513', '#a0522d', '#ff8c00', '#daa520'];
    class Leaf {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -30;
        this.w = Math.random() * 16 + 8; this.h = Math.random() * 10 + 5;
        this.speed = Math.random() * 1.5 + 0.5; this.opacity = Math.random() * 0.7 + 0.2;
        this.wobble = Math.random() * Math.PI * 2; this.wobbleSpeed = Math.random() * 0.025;
        this.angle = Math.random() * Math.PI * 2; this.angleSpeed = (Math.random() - 0.5) * 0.04;
        this.color = autumnColors[Math.floor(Math.random() * autumnColors.length)];
      }
      update() {
        this.y += this.speed; this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * 2; this.angle += this.angleSpeed;
        if (this.y > height + 30) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle);
        ctx.globalAlpha = this.opacity; ctx.fillStyle = this.color;
        ctx.beginPath(); ctx.ellipse(0, 0, this.w, this.h, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.2)'; ctx.lineWidth = 0.8;
        ctx.beginPath(); ctx.moveTo(-this.w, 0); ctx.lineTo(this.w, 0); ctx.stroke(); ctx.restore();
      }
    }

    // ─── MAKAR SANKRANTI (Flying Kites) ──────────────────────────────────────
    const kiteColors = ['#ff0000', '#ff6600', '#ffcc00', '#00cc00', '#0066ff', '#9900ff'];
    class Kite {
      constructor() { this.reset(); this.y = Math.random() * height * 0.6; }
      reset() {
        this.x = -80; this.y = Math.random() * height * 0.6;
        this.vx = Math.random() * 1.5 + 0.8; this.vy = (Math.random() - 0.5) * 0.4;
        this.size = Math.random() * 25 + 15;
        this.color = kiteColors[Math.floor(Math.random() * kiteColors.length)];
        this.color2 = kiteColors[Math.floor(Math.random() * kiteColors.length)];
        this.angle = Math.random() * 0.4 - 0.2;
        this.wobble = 0; this.wobbleSpeed = Math.random() * 0.03 + 0.01; this.tailPhase = 0;
      }
      update() {
        this.x += this.vx; this.wobble += this.wobbleSpeed;
        this.vy = Math.sin(this.wobble) * 0.5; this.y += this.vy; this.tailPhase += 0.1;
        if (this.x > width + 100) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle + Math.sin(this.wobble) * 0.1);
        ctx.fillStyle = this.color; ctx.beginPath();
        ctx.moveTo(0, -this.size); ctx.lineTo(this.size * 0.6, 0); ctx.lineTo(0, this.size * 0.4); ctx.closePath(); ctx.fill();
        ctx.fillStyle = this.color2; ctx.beginPath();
        ctx.moveTo(0, -this.size); ctx.lineTo(-this.size * 0.6, 0); ctx.lineTo(0, this.size * 0.4); ctx.closePath(); ctx.fill();
        ctx.fillStyle = this.color; ctx.beginPath();
        ctx.moveTo(-this.size * 0.6, 0); ctx.lineTo(this.size * 0.6, 0); ctx.lineTo(0, this.size); ctx.closePath(); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.3)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(0, this.size);
        for (let i = 0; i < 5; i++) ctx.lineTo(Math.sin(this.tailPhase + i * 0.8) * 10, this.size + (i + 1) * 12);
        ctx.stroke(); ctx.restore();
      }
    }

    // ─── CHRISTMAS (Stars) ─────────────────────────────────────────────────────
    class ChristmasStar {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = Math.random() * height;
        this.size = Math.random() * 4 + 1; this.opacity = Math.random() * 0.8 + 0.1;
        this.twinkle = Math.random() * Math.PI * 2; this.twinkleSpeed = Math.random() * 0.06 + 0.02;
        this.color = Math.random() > 0.5 ? '#ffd700' : '#ff4444';
      }
      update() { this.twinkle += this.twinkleSpeed; }
      draw(ctx) {
        const t = 0.3 + 0.7 * Math.abs(Math.sin(this.twinkle));
        ctx.save(); ctx.translate(this.x, this.y); ctx.globalAlpha = this.opacity * t;
        ctx.fillStyle = this.color; ctx.beginPath();
        for (let i = 0; i < 8; i++) {
          const angle = (i * Math.PI) / 4; const r = i % 2 === 0 ? this.size : this.size * 0.4;
          ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
        }
        ctx.closePath(); ctx.fill(); ctx.restore();
      }
    }

    // ─── TEEJ (Rain Drops + Green Leaves) ────────────────────────────────────
    class RainDrop {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -20;
        this.length = Math.random() * 15 + 8; this.speed = Math.random() * 6 + 6;
        this.opacity = Math.random() * 0.4 + 0.2;
        this.color = Math.random() > 0.5 ? 'rgba(82, 201, 124,' : 'rgba(152, 251, 152,';
      }
      update() { this.y += this.speed; if (this.y > height + this.length) this.reset(); }
      draw(ctx) {
        ctx.save(); ctx.globalAlpha = this.opacity;
        ctx.strokeStyle = this.color + this.opacity + ')';
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(this.x, this.y); ctx.lineTo(this.x - 1, this.y - this.length); ctx.stroke();
        ctx.restore();
      }
    }
    class TeejLeaf {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -30;
        this.size = Math.random() * 14 + 6; this.speed = Math.random() * 1.5 + 0.4;
        this.opacity = Math.random() * 0.6 + 0.3;
        this.wobble = Math.random() * Math.PI * 2; this.wobbleSpeed = Math.random() * 0.03;
        this.angle = Math.random() * Math.PI * 2; this.angleSpeed = (Math.random() - 0.5) * 0.03;
        const g = Math.floor(Math.random() * 60 + 120);
        this.color = `rgba(30, ${g}, 50,`;
      }
      update() {
        this.y += this.speed; this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * 1.5; this.angle += this.angleSpeed;
        if (this.y > height + 30) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle);
        ctx.globalAlpha = this.opacity; ctx.fillStyle = this.color + this.opacity + ')';
        ctx.beginPath();
        ctx.moveTo(0, this.size);
        ctx.bezierCurveTo(this.size * 0.8, this.size * 0.5, this.size * 0.8, -this.size * 0.5, 0, -this.size);
        ctx.bezierCurveTo(-this.size * 0.8, -this.size * 0.5, -this.size * 0.8, this.size * 0.5, 0, this.size);
        ctx.fill();
        ctx.strokeStyle = `rgba(0, 80, 20, ${this.opacity * 0.5})`; ctx.lineWidth = 0.8;
        ctx.beginPath(); ctx.moveTo(0, -this.size); ctx.lineTo(0, this.size); ctx.stroke();
        ctx.restore();
      }
    }

    // ─── GANGAUR (Flower Petals + Sparkling Dots) ─────────────────────────────
    const gangaurColors = ['#fc5c7d', '#fbb034', '#ff6f61', '#e91e8c', '#ffcc02', '#c2185b'];
    class GangaurPetal {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = -30;
        this.size = Math.random() * 12 + 5; this.speed = Math.random() * 1.8 + 0.4;
        this.opacity = Math.random() * 0.7 + 0.2;
        this.wobble = Math.random() * Math.PI * 2; this.wobbleSpeed = Math.random() * 0.04 + 0.01;
        this.angle = Math.random() * Math.PI * 2; this.angleSpeed = (Math.random() - 0.5) * 0.05;
        this.color = gangaurColors[Math.floor(Math.random() * gangaurColors.length)];
      }
      update() {
        this.y += this.speed; this.wobble += this.wobbleSpeed;
        this.x += Math.sin(this.wobble) * 2.5; this.angle += this.angleSpeed;
        if (this.y > height + 30) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.translate(this.x, this.y); ctx.rotate(this.angle);
        ctx.globalAlpha = this.opacity; ctx.fillStyle = this.color;
        // 5-petal flower shape
        for (let p = 0; p < 5; p++) {
          ctx.save(); ctx.rotate((p * Math.PI * 2) / 5);
          ctx.beginPath(); ctx.ellipse(0, -this.size * 0.5, this.size * 0.3, this.size * 0.5, 0, 0, Math.PI * 2);
          ctx.fill(); ctx.restore();
        }
        // Center dot
        ctx.fillStyle = '#fff0a5'; ctx.beginPath(); ctx.arc(0, 0, this.size * 0.25, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      }
    }
    class GangaurSparkle {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = Math.random() * height;
        this.size = Math.random() * 2 + 0.5; this.opacity = Math.random() * 0.6 + 0.2;
        this.twinkle = Math.random() * Math.PI * 2; this.speed = Math.random() * 0.08 + 0.03;
        this.color = gangaurColors[Math.floor(Math.random() * gangaurColors.length)];
      }
      update() { this.twinkle += this.speed; }
      draw(ctx) {
        const t = 0.3 + 0.7 * Math.abs(Math.sin(this.twinkle));
        ctx.save(); ctx.translate(this.x, this.y); ctx.globalAlpha = this.opacity * t;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        for (let i = 0; i < 4; i++) {
          const a = (i * Math.PI) / 2;
          ctx.lineTo(Math.cos(a) * this.size, Math.sin(a) * this.size);
          ctx.lineTo(Math.cos(a + Math.PI / 4) * this.size * 0.4, Math.sin(a + Math.PI / 4) * this.size * 0.4);
        }
        ctx.closePath(); ctx.fill(); ctx.restore();
      }
    }

    // ─── ROYAL RAJASTHAN (Desert Sand + Gold Particles) ──────────────────────
    class SandParticle {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = -10; this.y = Math.random() * height;
        this.size = Math.random() * 2 + 0.5;
        this.vx = Math.random() * 3 + 1; this.vy = (Math.random() - 0.5) * 0.5;
        this.opacity = Math.random() * 0.5 + 0.1;
        const r = Math.floor(200 + Math.random() * 55);
        const g = Math.floor(150 + Math.random() * 50);
        this.color = `rgba(${r}, ${g}, 50,`;
      }
      update() {
        this.x += this.vx; this.y += this.vy;
        if (this.x > width + 10) this.reset();
      }
      draw(ctx) {
        ctx.save(); ctx.globalAlpha = this.opacity;
        ctx.fillStyle = this.color + this.opacity + ')';
        ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      }
    }
    class GoldStar {
      constructor() { this.reset(); this.y = Math.random() * height; }
      reset() {
        this.x = Math.random() * width; this.y = Math.random() * height;
        this.size = Math.random() * 4 + 1.5; this.opacity = Math.random() * 0.7 + 0.2;
        this.twinkle = Math.random() * Math.PI * 2; this.speed = Math.random() * 0.05 + 0.02;
        this.vx = Math.random() * 0.15 - 0.075; this.vy = Math.random() * 0.1 - 0.05;
      }
      update() {
        this.twinkle += this.speed; this.x += this.vx; this.y += this.vy;
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
      }
      draw(ctx) {
        const t = 0.4 + 0.6 * Math.abs(Math.sin(this.twinkle));
        ctx.save(); ctx.translate(this.x, this.y); ctx.globalAlpha = this.opacity * t;
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size * 2.5);
        gradient.addColorStop(0, '#ffd700'); gradient.addColorStop(0.4, '#d4a017'); gradient.addColorStop(1, '#d4a01700');
        ctx.fillStyle = gradient; ctx.beginPath(); ctx.arc(0, 0, this.size * 2.5, 0, Math.PI * 2); ctx.fill();
        // 8-point star core
        ctx.fillStyle = '#ffe066'; ctx.beginPath();
        for (let i = 0; i < 8; i++) {
          const a = (i * Math.PI) / 4; const r = i % 2 === 0 ? this.size : this.size * 0.4;
          ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        }
        ctx.closePath(); ctx.fill(); ctx.restore();
      }
    }

    // ─── INITIALIZE ──────────────────────────────────────────────────────────
    const initEffects = () => {
      elements = []; fireworks = [];
      switch (themeName) {
        case 'Winter':      for (let i = 0; i < 100; i++) elements.push(new Snowflake()); break;
        case 'Christmas':
          for (let i = 0; i < 70; i++) elements.push(new Snowflake());
          for (let i = 0; i < 30; i++) elements.push(new ChristmasStar()); break;
        case 'Diwali':
          for (let i = 0; i < 14; i++) elements.push(new DiwaliGlowOrb());
          break; // dynamic fireworks
        case 'Holi':
          for (let i = 0; i < 8; i++) elements.push(new ColorBlob());
          for (let i = 0; i < 25; i++) elements.push(new ColorDrop()); break;
        case 'Navratri':    for (let i = 0; i < 60; i++) elements.push(new Petal()); break;
        case 'Spring':      for (let i = 0; i < 50; i++) elements.push(new BlossomPetal()); break;
        case 'Summer':      for (let i = 0; i < 30; i++) elements.push(new SummerBubble()); break;
        case 'Autumn':      for (let i = 0; i < 45; i++) elements.push(new Leaf()); break;
        case 'Eid':         for (let i = 0; i < 60; i++) elements.push(new EidStar()); break;
        case 'Makar Sankranti': for (let i = 0; i < 5; i++) elements.push(new Kite()); break;
        // ── Rajasthan themes ──
        case 'Teej':
          for (let i = 0; i < 80; i++) elements.push(new RainDrop());
          for (let i = 0; i < 20; i++) elements.push(new TeejLeaf()); break;
        case 'Gangaur':
          for (let i = 0; i < 50; i++) elements.push(new GangaurPetal());
          for (let i = 0; i < 40; i++) elements.push(new GangaurSparkle()); break;
        case 'Royal Rajasthan':
          for (let i = 0; i < 150; i++) elements.push(new SandParticle());
          for (let i = 0; i < 40; i++) elements.push(new GoldStar()); break;
        default: break;
      }
    };
    initEffects();

    // ─── RENDER LOOP ─────────────────────────────────────────────────────────
    const render = () => {
      if (themeName === 'Diwali') {
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = 'rgba(26, 9, 47, 0.045)'; ctx.fillRect(0, 0, width, height);

        for (let i = 0; i < elements.length; i++) {
          const ambient = elements[i];
          if (ambient instanceof DiwaliGlowOrb) {
            ambient.update();
            ambient.draw(ctx);
          }
        }

        if (Math.random() < 0.048 && fireworks.length < 4) fireworks.push(new DiwaliRocket());
        for (let i = fireworks.length - 1; i >= 0; i--) {
          fireworks[i].update(); fireworks[i].draw(ctx);
          if (fireworks[i].exploded()) {
            elements.push(...fireworks[i].explode()); fireworks.splice(i, 1);
          }
        }
        for (let i = elements.length - 1; i >= 0; i--) {
          const item = elements[i];
          if (item instanceof DiwaliParticle || item instanceof DiwaliEmber) {
            item.update(); item.draw(ctx);
            if (item.life <= 0) elements.splice(i, 1);
          }
        }
      } else if (themeName === 'Holi') {
        ctx.clearRect(0, 0, width, height);
        elements.filter(e => e instanceof ColorBlob).forEach(e => { e.update(); e.draw(ctx); });
        elements.filter(e => e instanceof ColorDrop).forEach(e => { e.update(); e.draw(ctx); });
      } else if (themeName === 'Makar Sankranti') {
        ctx.clearRect(0, 0, width, height);
        elements.forEach(el => { el.update(); el.draw(ctx); });
        if (elements.length < 5 && Math.random() < 0.015) elements.push(new Kite());
      } else {
        ctx.clearRect(0, 0, width, height);
        elements.forEach(el => { el.update(); el.draw(ctx); });
      }
      animationFrameId = requestAnimationFrame(render);
    };
    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [themeName]);

  if (themeName === 'Normal') return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 0, pointerEvents: 'none',
      }}
    />
  );
};

export default ThemeEffects;
